import {HttpErrorResponse} from '@angular/common/http';
import { Injectable, RendererFactory2, inject } from '@angular/core';
import {Router} from '@angular/router';
import {StorageKeys} from '@app/core/constants/storage-keys';
import {LocalStorageService} from '@app/core/services/local-storage.service';
import {FileUtils} from '@app/core/utils/file.utils';
import {ToastMessageUtils} from '@app/core/utils/toast-message.utils';
import {EToastMessageOptions, EToastMessageSeverity} from '@app/shared/enums/toast-message.enum';
import {ILanguage} from '@app/shared/models/language.model';
import {selectSelectedLanguage} from '@app/shared/state/language/language.selector';
import {environment} from '@environments/environment';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {MessageService} from 'primeng/api';
import {catchError, map, of, retry, switchMap, withLatestFrom} from 'rxjs';
import {ECheckoutStatus} from '../enums/checkout-status.enum';
import {CheckoutStep} from '../enums/checkout-step.enum';
import {ICustomerOrganization} from '../models/customer-organization.model';
import {Order} from '../models/order-request.model';
import {OrderResponse} from '../models/order-response.model';
import {CheckoutService} from '../services/checkout.service';
import {
  CheckoutActions,
  cancelOrderAction,
  cancelOrderEffectError,
  cancelOrderEffectFailure,
  cancelOrderEffectSuccess,
  consentPdfAction,
  consentPdfEffectError,
  consentPdfEffectFailure,
  consentPdfEffectSuccess,
  getCustomerOrganization,
  getCustomerOrganizationFailure,
  getCustomerOrganizationSuccess,
  getOrder,
  getOrderFailure,
  getOrderSuccess,
  getPatientRelationTypes,
  getPatientRelationTypesFailure,
  getPatientRelationTypesSuccess,
  getSampleTypesFailure,
  getSampleTypesFromOrderDetailsPage,
  getSampleTypesFromPatientInfoPage,
  getSampleTypesSuccess,
  saveOrder,
  saveOrderFailure,
  saveOrderSuccess,
  saveTestOrder,
  updateOrderFailure,
  updateOrderStatus,
  updateOrderStatusFailure,
  updateOrderStatusSuccess,
  updateOrderSuccess,
  updateOrderType,
} from './checkout.actions';
import {selectOrderStep} from './checkout.selector';

@Injectable()
export class CheckoutEffects {
  private readonly actions$ = inject(Actions);
  private readonly checkoutService = inject(CheckoutService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly rendererFactory = inject(RendererFactory2);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly localStorageService = inject(LocalStorageService);

  globalToast = 'global-toast';
  static readonly MAX_GETORDER_RETRIES = 3;
  orderListUrl = '/my-orders';
  selfRelationId = Number(environment.selfRelationId);

  getOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.GetOrder),
      switchMap((action: ReturnType<typeof getOrder>) => this.getOrder(action))
    )
  );

  saveOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.SaveOrder),
      withLatestFrom(this.store.select(selectOrderStep)),
      switchMap(([action]: [ReturnType<typeof saveOrder>, CheckoutStep]) => {
        return this.saveOrder(action);
      })
    )
  );

  saveTestVarientOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.SaveTestOrder),
      switchMap((action: ReturnType<typeof saveTestOrder>) => {
        return this.saveTestVarientOrder(action);
      })
    )
  );

  updateOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType<updateOrderType>(
        CheckoutActions.UpdateOrder,
        CheckoutActions.UpdateOrderFromOrderInfo,
        CheckoutActions.UpdateOrderFromBillingInfo,
        CheckoutActions.UpdateOrderFromConfirmOrder,
        CheckoutActions.UpdateOrderFromPatientInfo,
        CheckoutActions.UpdateOrderFromClinicalInfo,
        CheckoutActions.UpdateOrderFromConsentInfo
      ),
      withLatestFrom(this.store.select(selectOrderStep)),
      switchMap(([action]: [updateOrderType, CheckoutStep]) => {
        return this.updateOrder(action);
      })
    )
  );

  updateOrderStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.UpdateOrderStatus),
      switchMap((action: ReturnType<typeof updateOrderStatus>) => {
        return this.updateOrderStatus(action);
      })
    )
  );

  getCustomerOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.GetCustomerOrganization),
      switchMap((action: ReturnType<typeof getCustomerOrganization>) => this.getCustomerOrganization(action))
    )
  );

  getSampleTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.GetSampleTypesFromPatientInfoPage, CheckoutActions.GetSampleTypesFromOrderDetailsPage),
      switchMap(
        (action: ReturnType<typeof getSampleTypesFromPatientInfoPage | typeof getSampleTypesFromOrderDetailsPage>) => {
          if (action.type === CheckoutActions.GetSampleTypesFromPatientInfoPage) {
            return this.getSampleTypes(action.productId);
          } else {
            return this.getSampleTypes();
          }
        }
      )
    )
  );

  getPatientRelationTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.GetPatientRelationTypes),
      withLatestFrom(this.store.select(selectSelectedLanguage)),
      switchMap(([action, language]: [ReturnType<typeof getPatientRelationTypes>, ILanguage | undefined]) => {
        const currentLanguage = this.localStorageService.getItem(StorageKeys.currentLanguage);
        const selectedLanguage = language?.id ?? currentLanguage;
        return this.getPatientRelationTypes(action.enabled, `${selectedLanguage}`);
      })
    )
  );

  private getOrder(action: ReturnType<typeof getOrder>) {
    return this.checkoutService.getOrder(action.orderId)?.pipe(
      map((orderData: OrderResponse) => {
        orderData = {
          ...orderData,
          lastCompletedStep: orderData.lastCompletedStep ?? -1,
          currentStep: orderData.lastCompletedStep ?? 0,
        };
        return getOrderSuccess({
          orderData: orderData,
          orderFormData: this.processOrderFormData(orderData),
        });
      }),
      retry(CheckoutEffects.MAX_GETORDER_RETRIES),
      catchError((error: HttpErrorResponse) => {
        const errMsg = this.getErrorMessage(error.status);
        this.showErrorMessage(errMsg);
        return of(getOrderFailure());
      })
    );
  }

  private getCustomerOrganization(action: ReturnType<typeof getCustomerOrganization>) {
    return this.checkoutService.getCustomerOrganization(action.customerId)?.pipe(
      map((customerOrganization: ICustomerOrganization[]) => {
        return getCustomerOrganizationSuccess({
          customerOrganization: customerOrganization,
        });
      }),
      catchError(() => of(getCustomerOrganizationFailure()))
    );
  }

  private saveTestVarientOrder(action: ReturnType<typeof saveTestOrder>) {
    return this.checkoutService.createOrder(action.orderRequest)?.pipe(
      map((orderData: Order) => {
        this.router.navigate(['/checkout'], {
          queryParams: {id: orderData?.id},
        });
        return saveOrderSuccess({
          orderData: {
            ...orderData,
          },
          checkoutStatus: ECheckoutStatus.Success,
        });
      }),
      catchError((error: HttpErrorResponse) => {
        const errMsg = this.getErrorMessage(error.status);
        this.showErrorMessage(errMsg);
        return of(saveOrderFailure({checkoutStatus: ECheckoutStatus.Failed}));
      })
    );
  }

  private saveOrder(action: ReturnType<typeof saveOrder>) {
    const request = {
      ...action.orderRequest,
    };
    return this.checkoutService.createOrder(request)?.pipe(
      map((orderData: Order) => {
        this.orderSaveSuccessMessage();
        return saveOrderSuccess({
          orderData: {
            ...orderData,
          },
          checkoutStatus: ECheckoutStatus.Success,
        });
      }),
      catchError(() => {
        return of(saveOrderFailure({checkoutStatus: ECheckoutStatus.Failed}));
      })
    );
  }

  private updateOrder(action: updateOrderType) {
    const request = {
      ...action.orderRequest,
    };
    return this.checkoutService.updateOrder(request)?.pipe(
      map((orderData: OrderResponse) => {
        this.orderSaveSuccessMessage(action, orderData);

        if (action.isDraft) {
          this.router.navigate([this.orderListUrl]);
        }

        return updateOrderSuccess({
          orderData: {
            ...orderData,
          },
          orderFormData: this.processOrderFormData(orderData),
          checkoutStatus: this.processSuccessCheckoutStatus(action),
        });
      }),
      catchError((error: HttpErrorResponse) => {
        const errMsg = this.getErrorMessage(error.status);
        this.showErrorMessage(errMsg);
        return of(
          updateOrderFailure({
            checkoutStatus: this.processErrorCheckoutStatus(action),
          })
        );
      })
    );
  }

  private updateOrderStatus(action: ReturnType<typeof updateOrderStatus>) {
    return this.checkoutService.updateOrderStatus(action.orderId, action.statusId)?.pipe(
      map((orderData: Order) => {
        this.orderSaveSuccessMessage();
        return updateOrderStatusSuccess({orderData: orderData});
      }),
      catchError((error: HttpErrorResponse) => {
        const errMsg = this.getErrorMessage(error.status);
        this.showErrorMessage(errMsg);
        return of(updateOrderStatusFailure());
      })
    );
  }

  private orderSaveSuccessMessage(action?: updateOrderType, orderData?: OrderResponse): void {
    let messageDetail = this.translate.instant('Checkout.OrderSaveSuccess');
    if (action && action.type === CheckoutActions.UpdateOrderFromConfirmOrder) {
      messageDetail = this.translate.instant('Checkout.OrderSubmitSuccess', {
        orderId: orderData?.id.toString(),
      });
      this.showSuccessMessage(messageDetail, 5000);
      this.router.navigate([this.orderListUrl]);
    } else {
      this.showSuccessMessage(messageDetail);
    }
  }

  private getErrorMessage(statusCode: number): string {
    let errorMessage = '';
    switch (statusCode) {
      case 400:
        errorMessage = this.translate.instant('Common.400_Error');
        break;
      case 401:
        errorMessage = this.translate.instant('Common.401_Error');
        break;
      case 404:
        errorMessage = this.translate.instant('Common.404_Error');
        break;
      case 403:
        errorMessage = this.translate.instant('Common.403_Error');
        break;
      case 500:
        errorMessage = this.translate.instant('Common.500_Error');
        break;
      default:
        errorMessage = this.translate.instant('Common.Failure');
        break;
    }

    return errorMessage;
  }

  private showErrorMessage(errMsg: string): void {
    ToastMessageUtils.showMessage({
      messageService: this.messageService,
      toastKey: this.globalToast,
      severity: EToastMessageSeverity.Error,
      messageDetail: errMsg,
      closable: true,
      life: EToastMessageOptions.LifeTime,
    });
  }

  private showSuccessMessage(msg: string, life?: number): void {
    ToastMessageUtils.showMessage({
      messageService: this.messageService,
      toastKey: this.globalToast,
      severity: EToastMessageSeverity.Success,
      messageDetail: msg,
      closable: true,
      life: life || EToastMessageOptions.LifeTime,
    });
  }

  private processOrderFormData(orderResponse: OrderResponse): Order {
    const order = orderResponse as Order;
    order.customerFirstName = orderResponse.customer?.firstName ?? orderResponse.customerFirstName;
    order.customerLastName = orderResponse.customer?.lastName ?? orderResponse.customerLastName;
    order.currentStep = orderResponse.currentStep ?? 0;
    order.relatedPatientsList = order.relatedPatientsList ?? [];
    return order;
  }

  private processSuccessCheckoutStatus(action: updateOrderType): ECheckoutStatus {
    let checkoutStatus = ECheckoutStatus.Initial;
    switch (action.type) {
      case CheckoutActions.UpdateOrder:
        checkoutStatus = ECheckoutStatus.Success;
        break;
      case CheckoutActions.UpdateOrderFromOrderInfo:
        checkoutStatus = ECheckoutStatus.SuccessFromOrderInfo;
        break;
      case CheckoutActions.UpdateOrderFromBillingInfo:
        checkoutStatus = ECheckoutStatus.SuccessFromBillingInfo;
        break;
      case CheckoutActions.UpdateOrderFromConfirmOrder:
        checkoutStatus = ECheckoutStatus.SuccessFromConfirmOrder;
        break;
      case CheckoutActions.UpdateOrderFromPatientInfo:
        checkoutStatus = ECheckoutStatus.SuccessFromPatientInfo;
        break;
      case CheckoutActions.UpdateOrderFromClinicalInfo:
        checkoutStatus = ECheckoutStatus.SuccessFromClinicalInfo;
        break;
      case CheckoutActions.UpdateOrderFromConsentInfo:
        checkoutStatus = ECheckoutStatus.SuccessFromConsentInfo;
        break;
    }

    return checkoutStatus;
  }

  private processErrorCheckoutStatus(action: updateOrderType): ECheckoutStatus {
    let checkoutStatus = ECheckoutStatus.Initial;
    switch (action.type) {
      case CheckoutActions.UpdateOrder:
        checkoutStatus = ECheckoutStatus.Failed;
        break;
      case CheckoutActions.UpdateOrderFromOrderInfo:
        checkoutStatus = ECheckoutStatus.ErrorFromOrderInfo;
        break;
      case CheckoutActions.UpdateOrderFromBillingInfo:
        checkoutStatus = ECheckoutStatus.ErrorFromBillingInfo;
        break;
      case CheckoutActions.UpdateOrderFromConfirmOrder:
        checkoutStatus = ECheckoutStatus.ErrorFromConfirmOrder;
        break;
      case CheckoutActions.UpdateOrderFromPatientInfo:
        checkoutStatus = ECheckoutStatus.ErrorFromPatientInfo;
        break;
      case CheckoutActions.UpdateOrderFromClinicalInfo:
        checkoutStatus = ECheckoutStatus.ErrorFromClinicalInfo;
        break;
      case CheckoutActions.UpdateOrderFromConsentInfo:
        checkoutStatus = ECheckoutStatus.ErrorFromConsentInfo;
        break;
    }

    return checkoutStatus;
  }

  consentPdf$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.ConsentPdf),
      switchMap((action: ReturnType<typeof consentPdfAction>) => this.consentPdf(action))
    )
  );

  private consentPdf(action: ReturnType<typeof consentPdfAction>) {
    return this.checkoutService
      .getConsentPdf(action.request.orderId, action.request.productId, action.request.languageId)
      .pipe(
        map((response: ArrayBuffer | null) => {
          if (response) {
            FileUtils.downloadFile({
              data: response,
              filename:
                this.checkoutService.fileName || `${action.request.languageId}_${action.request.productId}_Consent.pdf`,
              renderer: this.rendererFactory.createRenderer(null, null),
              type: this.checkoutService.contentType,
            });
          }
          return consentPdfEffectSuccess();
        }),
        catchError((error: HttpErrorResponse) => {
          const errMsg = this.getErrorMessage(error.status);
          this.showErrorMessage(errMsg);
          if (Array.isArray(error.error)) {
            return this.handleArrayErrors(error);
          } else if (typeof error.error === 'string') {
            return this.handleStringErrors(error);
          } else {
            return this.handleErrors(error);
          }
        })
      );
  }

  private handleArrayErrors(errorResult: HttpErrorResponse) {
    return of(consentPdfEffectFailure({reason: errorResult.error.join(', ')}));
  }

  private handleStringErrors(errorResult: HttpErrorResponse) {
    return of(consentPdfEffectFailure({reason: errorResult.error}));
  }

  private handleErrors(errorResult: HttpErrorResponse) {
    return of(consentPdfEffectError(errorResult));
  }

  cancelOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.CancelOrder),
      switchMap((action: ReturnType<typeof cancelOrderAction>) => this.cancelOrder(action))
    )
  );

  private cancelOrder(action: ReturnType<typeof cancelOrderAction>) {
    return this.checkoutService.cancelOrder(action.request.id).pipe(
      map((response: boolean) => {
        this.orderCancelSuccessMessage();
        this.router.navigate([this.orderListUrl]);
        return cancelOrderEffectSuccess({response});
      }),
      catchError((error: HttpErrorResponse) => {
        const errMsg = this.getErrorMessage(error.status);
        this.showErrorMessage(errMsg);
        if (Array.isArray(error.error)) {
          return of(cancelOrderEffectFailure({reason: error.error.join(', ')}));
        } else if (typeof error.error === 'string') {
          return of(cancelOrderEffectFailure({reason: error.error}));
        } else {
          return of(cancelOrderEffectError(error));
        }
      })
    );
  }

  private getSampleTypes(productId?: number) {
    return this.checkoutService.getSampleTypes(productId).pipe(
      map(sampleTypes => getSampleTypesSuccess({sampleTypes})),
      catchError(() => of(getSampleTypesFailure()))
    );
  }

  private getPatientRelationTypes(enabled: boolean, languageId: string) {
    return this.checkoutService.getPatientRelationTypes(enabled, languageId).pipe(
      map(patientRelationTypes => {
        const patientRelationsWithoutSelf = patientRelationTypes?.filter(
          patientRelationType => patientRelationType.id !== this.selfRelationId
        );
        return getPatientRelationTypesSuccess({
          patientRelationTypes: patientRelationsWithoutSelf,
        });
      }),
      catchError(() => of(getPatientRelationTypesFailure()))
    );
  }

  private orderCancelSuccessMessage(): void {
    this.showSuccessMessage(this.translate.instant('Checkout.OrderCancelSuccess'));
  }
}
